import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { DiscountBeneficiaryType, DiscountTargetType, DiscountValueType } from '@/constants';
import AppDatePicker from '@/components/FormItems/DatePicker';
import RouteInput from '@/components/FormItems/RouteInput';
import CompanyTicketClassInput from '@/components/FormItems/CompanyTicketClassInput';
import TariffParameterInput from '@/components/FormItems/TariffParameterInput';
import BaseTripInput from '@/components/FormItems/BaseTripInput';
import SegmentInput from '@/components/FormItems/SegmentInput';
import moment from 'moment';
import PassengerInput from '@/components/FormItems/PassengerInput';
import { DiscountModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';

const PAGE_TITLE = 'discountsPage_Title';

const columns: IColumnType<DiscountModel>[] = [
  {
    title: 'discountsPage_Name',
    dataIndex: 'name',
    ellipsis: true,
    defaultSortOrder: 'ascend',
    width: 300,
  },
  {
    title: 'discountsPage_StartDate',
    dataIndex: 'startDate',
    sorter: true,
    ellipsis: true,
    render: v => formatDisplayDate(v),
    width: 120,
  },
  {
    title: 'discountsPage_EndDate',
    dataIndex: 'endDate',
    sorter: true,
    ellipsis: true,
    render: v => formatDisplayDate(v),
    width: 120,
  },
  {
    title: 'discountsPage_Value',
    dataIndex: 'value',
    ellipsis: true,
    sorter: true,
    width: 100,
  },
  {
    title: 'discountsPage_ValueType',
    dataIndex: 'valueType',
    ellipsis: true,
    sorter: true,
    width: 120,

    render: v => DiscountValueType.__getValue(v),
  },
  {
    title: 'discountsPage_beneficiaryType',
    dataIndex: 'beneficiaryType',
    ellipsis: true,
    sorter: true,
    width: 120,

    render: v => DiscountBeneficiaryType.__getValue(v),
  },
  {
    title: 'discountsPage_targetType',
    dataIndex: 'targetType',
    ellipsis: true,
    sorter: true,
    width: 120,
    render: v => DiscountTargetType.__getValue(v),
  },
];

const filters: Array<IFilterItem> = [
  { type: 'DATE', name: 'startDate', label: 'discountsPage_StartDate', mdSize: 3, xlSize: 3, xxlSize: 3, parseValue: v => formatDate(v) },
  { type: 'DATE', name: 'endDate', label: 'discountsPage_EndDate', mdSize: 3, xlSize: 3, xxlSize: 3, parseValue: v => formatDate(v) },
  { type: 'TEXT', name: 'name', label: 'discountsPage_Name' },
  { type: 'LIST', name: 'beneficiaryType', label: 'discountsPage_beneficiaryType', listData: DiscountBeneficiaryType.__getSelectOptions() },
  { type: 'LIST', name: 'targetType', label: 'discountsPage_targetType', listData: DiscountTargetType.__getSelectOptions() },
];

const DiscountsPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canUpdate = useHasPermissions([APP_PERMISSIONS.ManageDiscounts]);
  const createEntity = values => {
    setLoading(true);
    apiSdk.DiscountService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.DiscountService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editDiscount = (id: string) => {
    setIsNewForm(false);
    setFormVisible(true);
    setLoading(true);
    apiSdk.DiscountService.getEntityForUpdate({ id: id })
      .then(res => {
        form.setFieldsValue({
          ...res,
          allPassengers: !res.passengerIds,
          allTariffParameter: !res.beneficiaryEntityId,
          allTicketClass: !res.beneficiaryEntityId,
          allRoutes: !res.routeId,
          allTrips: !res.tripId,
          allSegments: !res.segmentId,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const beneficiaryType = Form.useWatch('beneficiaryType', form);
  const routeId = Form.useWatch('routeId', form);
  const tripId = Form.useWatch('tripId', form);

  const allPassengers = Form.useWatch('allPassengers', form);
  const allTariffParameter = Form.useWatch('allTariffParameter', form);
  const allTicketClass = Form.useWatch('allTicketClass', form);

  const allRoutes = Form.useWatch('allRoutes', form);

  const allTrips = Form.useWatch('allTrips', form);

  const allSegments = Form.useWatch('allSegments', form);

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setIsNewForm(true);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : undefined
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<DiscountModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/commons/discounts/get-discounts"
          bordered
          order_by="name"
          order_by_asc={true}
          allowEdit={canUpdate}
          onEdit={record => {
            editDiscount(record.id || '');
          }}
          allowDelete={canUpdate}
          delete_url="/commons/discounts/delete"
          filters={filters}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.medium}
            className="nta-custom-form"
            title={isNewForm ? t('discountsPage_CreateDiscount') : t('discountsPage_UpdateDiscount')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form
                form={form}
                size="middle"
                layout="horizontal"
                labelAlign="right"
                labelCol={{ md: 10 }}
                initialValues={{
                  allPassengers: true,
                  allTariffParameter: true,
                  allTicketClass: true,
                  allRoutes: true,
                  allTrips: true,
                  allSegments: true,
                }}
              >
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={12}>
                    <Form.Item label={t('discountsPage_Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item noStyle dependencies={['endDate']}>
                      {({ getFieldValue }) => {
                        const endDate = getFieldValue('endDate');
                        return (
                          <Form.Item label={t('discountsPage_StartDate')} name="startDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <AppDatePicker
                              className="w-100"
                              disabledDate={d => {
                                if (endDate) {
                                  return d.endOf('day') > moment(endDate).endOf('day');
                                }
                                return false;
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item noStyle dependencies={['startDate']}>
                      {({ getFieldValue }) => {
                        const startDate = getFieldValue('startDate');
                        return (
                          <Form.Item label={t('discountsPage_EndDate')} name="endDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                            <AppDatePicker
                              className="w-100"
                              disabledDate={d => {
                                if (startDate) {
                                  return d.endOf('day') < moment(startDate).endOf('day');
                                }
                                return false;
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item label={t('discountsPage_ValueType')} name="valueType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={DiscountValueType.__getSelectOptions()} />
                    </Form.Item>
                    <Form.Item label={t('discountsPage_Value')} name="value" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item
                      label={t('discountsPage_beneficiaryType')}
                      name="beneficiaryType"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Select
                        options={DiscountBeneficiaryType.__getSelectOptions()}
                        onChange={() => {
                          form.setFieldValue('beneficiaryEntityId', null);
                        }}
                      />
                    </Form.Item>
                    {beneficiaryType === DiscountBeneficiaryType.Passenger ? (
                      <>
                        <Form.Item label="  " name="allPassengers" colon={false} valuePropName="checked">
                          <Checkbox>{t('discountsPage_AllPassengers')}</Checkbox>
                        </Form.Item>
                        {allPassengers ? null : (
                          <Form.Item
                            label={t('discountsPage_Passengers')}
                            name="passengerIds"
                            rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                          >
                            <PassengerInput mode="multiple" />
                          </Form.Item>
                        )}
                      </>
                    ) : null}
                    {beneficiaryType === DiscountBeneficiaryType.TariffParameter ? (
                      <>
                        <Form.Item label="  " name="allTariffParameter" colon={false} valuePropName="checked">
                          <Checkbox>{t('discountsPage_AllTariffParameters')}</Checkbox>
                        </Form.Item>
                        {allTariffParameter ? null : (
                          <Form.Item
                            label={t('discountsPage_TariffParameter')}
                            name="beneficiaryEntityId"
                            rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                          >
                            <TariffParameterInput />
                          </Form.Item>
                        )}
                      </>
                    ) : null}

                    {beneficiaryType === DiscountBeneficiaryType.TicketClass ? (
                      <>
                        <Form.Item label="  " name="allTicketClass" colon={false} valuePropName="checked">
                          <Checkbox>{t('discountsPage_AllTicketClasses')}</Checkbox>
                        </Form.Item>
                        {allTicketClass ? null : (
                          <Form.Item
                            label={t('discountsPage_TicketClass')}
                            name="beneficiaryEntityId"
                            rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                          >
                            <CompanyTicketClassInput />
                          </Form.Item>
                        )}
                      </>
                    ) : null}
                  </Col>
                  <Col md={12}>
                    <Form.Item label={t('discountsPage_targetType')} name="targetType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={DiscountTargetType.__getSelectOptions()} />
                    </Form.Item>

                    <Form.Item label="  " name="allRoutes" colon={false} valuePropName="checked">
                      <Checkbox>{t('discountsPage_AllRoutes')}</Checkbox>
                    </Form.Item>
                    {allRoutes ? null : (
                      <>
                        <Form.Item label={t('discountsPage_Route')} name="routeId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                          <RouteInput
                            onChange={() => {
                              form.setFieldValue('tripId', '');
                              form.setFieldValue('segmentId', '');
                            }}
                          />
                        </Form.Item>
                        <Form.Item label="  " name="allTrips" colon={false} valuePropName="checked">
                          <Checkbox>{t('discountsPage_AllTrips')}</Checkbox>
                        </Form.Item>
                        {allTrips ? null : (
                          <>
                            <Form.Item label={t('discountsPage_Trip')} name="tripId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                              <BaseTripInput
                                routeId={routeId}
                                disabled={!routeId}
                                onChange={() => {
                                  form.setFieldValue('segmentId', '');
                                }}
                              />
                            </Form.Item>
                            <Form.Item label="  " name="allSegments" colon={false} valuePropName="checked">
                              <Checkbox>{t('discountsPage_AllSegments')}</Checkbox>
                            </Form.Item>
                            {allSegments ? null : (
                              <Form.Item label={t('discountsPage_Segment')} name="segmentId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                <SegmentInput baseTripId={tripId} disabled={!tripId} />
                              </Form.Item>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default DiscountsPage;
